import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Button,
  Card,
  CardContent,
  useTheme,
  Avatar
} from "@mui/material";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../store/Index";
import { tokens } from "../../../../../theme";
import { StepComponentProps } from "../../AddBookingV2";
import StepHeader from "../StepHeader";
import { usePackagesAndServices } from "../usePackagesAndServices";

interface ServiceResponse {
  id: number;
  name: string;
  description: string | null;
  price: string | null;
  service_category: string;
  service_category_display: string;
  created_at: string;
  updated_at: string;
}

interface PaginatedServiceResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: ServiceResponse[];
}

interface PackageBaseService {
  id: number;
  name: string;
  price?: string | number;
}

interface PaginatedPackageResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: {
    id: number;
    name: string;
    package_type: string;
    base_price: string;
    description: string;
    package_logo?: string | null;
    created_at: string;
    updated_at: string;
    company: number;
    base_services: PackageBaseService[];
  }[];
}

const isPaginatedPackageResponse = (
  data: unknown,
): data is PaginatedPackageResponse => {
  return (
    typeof data === "object" &&
    data !== null &&
    "count" in data &&
    "next" in data &&
    "previous" in data &&
    "results" in data &&
    typeof (data as PaginatedPackageResponse).count === "number" &&
    Array.isArray((data as PaginatedPackageResponse).results) &&
    (data as PaginatedPackageResponse).results.every(
      (item) =>
        typeof item === "object" &&
        item !== null &&
        "id" in item &&
        "name" in item &&
        "base_price" in item &&
        "description" in item &&
        "base_services" in item &&
        typeof item.id === "number" &&
        typeof item.name === "string" &&
        typeof item.base_price === "string" &&
        typeof item.description === "string" &&
        Array.isArray(item.base_services),
    )
  );
};

const isPaginatedServiceResponse = (
  data: unknown,
): data is PaginatedServiceResponse => {
  return (
    typeof data === "object" &&
    data !== null &&
    "count" in data &&
    "results" in data &&
    Array.isArray((data as PaginatedServiceResponse).results) &&
    (data as PaginatedServiceResponse).results.every(
      (item) =>
        typeof item === "object" &&
        item !== null &&
        "id" in item &&
        "name" in item &&
        "price" in item &&
        "service_category" in item,
    )
  );
};

const PackageStep: React.FC<StepComponentProps> = ({
  bookingData,
  setBookingData,
  isEdit,
  onUpdate,
  submitting,
  bookingType,
}) => {
  const theme = useTheme();
  const themeState = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, themeState);

  const color400 = colors.primary[400];
  const color10 = colors.primary[10];
  const primaryGradient = `linear-gradient(135deg, ${color400} 0%, ${color10} 100%)`;

  const { packages, loading } = usePackagesAndServices(bookingType);

  const [editedTotal, setEditedTotal] = useState<number | string>(
    bookingData.total || 0,
  );
  const [isManualEdit, setIsManualEdit] = useState(false);
  const isInitialMount = useRef(true);
  const lastCalculatedTotal = useRef<number>(0);
  const currentPackageIdRef = useRef(bookingData.package?.id);

  useEffect(() => {
    if (packages.length > 0 && !currentPackageIdRef.current) {
      const defaultPackage = packages[0];
      const total = defaultPackage.price;
      lastCalculatedTotal.current = total;
      setEditedTotal(total);
      setBookingData((prev) => ({
        ...prev,
        package: { ...defaultPackage },
        package_total: total,
        additional_services_total: prev.additional_services_total ?? 0,
        total: total + (prev.additional_services_total ?? 0),
      }));
    }
  }, [packages, setBookingData]);

  // Recalculate total when package changes
  useEffect(() => {
    if (isManualEdit) return;
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const packageTotal =
      (bookingData.package?.price || 0) +
      (bookingData.package?.base_services?.reduce(
        (sum, s) => sum + Number(s.price || 0),
        0,
      ) || 0);
    const additionalTotal = bookingData.additional_services_total || 0;
    const finalTotal = parseFloat((packageTotal + additionalTotal).toFixed(2));

    if (Math.abs(lastCalculatedTotal.current - finalTotal) > 0.01) {
      lastCalculatedTotal.current = finalTotal;
      setEditedTotal(finalTotal);
      const timeoutId = setTimeout(() => {
        setBookingData((prev) => ({
          ...prev,
          total: finalTotal,
          package_total: packageTotal,
        }));
      }, 10);
      return () => clearTimeout(timeoutId);
    }
  }, [
    bookingData.package,
    bookingData.additional_services_total,
    isManualEdit,
    setBookingData,
  ]);

  useEffect(() => {
    currentPackageIdRef.current = bookingData.package?.id;
  }, [bookingData.package?.id]);

  useEffect(() => {
    setEditedTotal(parseFloat(String(bookingData.total)) || 0);
  }, [bookingData.total]);

  const handleTotalChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setIsManualEdit(true);
      const value = event.target.value.replace(/[^0-9.]/g, "");
      const newTotal = parseFloat(value) || 0;
      setEditedTotal(value);
      lastCalculatedTotal.current = newTotal;
      setBookingData((prev) => ({ ...prev, total: newTotal }));
    },
    [setBookingData],
  );

  const handlePackageCardClick = useCallback(
    (pkg: (typeof packages)[number]) => {
      setIsManualEdit(false);
      setBookingData((prev) => ({ ...prev, package: { ...pkg } }));
    },
    [setBookingData],
  );

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography sx={{ color: "text.secondary" }}>
          Loading packages...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 4 }}
    >
      
      <Box
      sx={{
        maxHeight: "55vh",
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
        gap: 4,
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": { display: "none" },
        pb: 2,
      }}
    >
      <StepHeader
        title="Select Package"
        isEdit={isEdit}
        onUpdate={onUpdate}
        submitting={submitting}
      />


      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 3,
          alignItems: "stretch",
        }}
      >
        {packages.length === 0 ? (
          <Typography sx={{ color: "text.secondary" }}>
            No packages available
          </Typography>
        ) : (
          packages.map((pkg) => {
            const isSelected = bookingData.package?.id === pkg.id;
            const currentPkgLogo = (pkg as typeof pkg & { package_logo?: string | null }).package_logo;
            return (
              <Card
                key={pkg.id}
                onClick={() => handlePackageCardClick(pkg)}
                sx={{
                  borderRadius: "16px",
                  border: isSelected ? `2px solid ${color400}` : `1px solid`,
                  borderColor: isSelected ? color400 : "divider",
                  cursor: "pointer",
                  backgroundColor: "background.paper",
                  boxShadow: isSelected ? `0px 8px 24px ${color400}22` : "none",
                  transition: "all 0.2s ease-in-out",
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    borderColor: color400,
                  },
                }}
              >
                <CardContent
                  sx={{
                    p: 3,
                    "&:last-child": { pb: 3 },
                    display: "flex",
                    flexDirection: "column",
                    flexGrow: 1,
                  }}
                >
                    <Avatar
                      src={currentPkgLogo || undefined}
                      imgProps={{
                        style: {
                          objectFit: "contain",
                          padding: currentPkgLogo ? "4px" : "0px",
                        },
                      }}
                      sx={{
                        mb: 2, 
                        background: currentPkgLogo
                          ? "transparent"
                          : primaryGradient,
                        color: "#fff",
                        width: 48,
                        height: 48,
                        boxShadow: `0 4px 12px ${color400}33`,
                        border: currentPkgLogo ? `1px solid ${color400}22` : "none",
                      }}
                    >
                      {/* Fallback to first letter of package name if no logo exists */}
                      {!currentPkgLogo && pkg.name ? pkg.name.charAt(0).toUpperCase() : ""}
                    </Avatar>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 700, mb: 0.5, color: "text.primary" }}
                    >
                      {pkg.name}
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{ fontWeight: 800, mb: 2, color: "text.primary" }}
                    >
                      ${pkg.price.toFixed(2)}
                    </Typography>

                  {pkg.contents && pkg.contents.length > 0 && (
                    <>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          fontWeight: 700,
                          display: "block",
                          mb: 1,
                          textTransform: "uppercase",
                        }}
                      >
                        Features
                      </Typography>
                      <Box
                        component="ul"
                        sx={{
                          p: 0,
                          m: 0,
                          listStyle: "none",
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.75,
                          mb: 2,
                          maxHeight: 120,
                          overflowY: "auto",
                          scrollbarWidth: "none",
                          "&::-webkit-scrollbar": { display: "none" },
                        }}
                      >
                        {pkg.contents.map((item, idx) => (
                          <Box
                            component="li"
                            key={idx}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: 4,
                                height: 4,
                                borderRadius: "50%",
                                backgroundColor: "text.primary",
                                flexShrink: 0,
                              }}
                            />
                            <Typography
                              variant="body2"
                              sx={{ color: "text.primary", fontSize: "13px" }}
                              dangerouslySetInnerHTML={{ __html: item }}
                            />
                          </Box>
                        ))}
                      </Box>
                    </>
                  )}

                  <Box sx={{ mt: "auto" }}>
                    <Button
                      fullWidth
                      variant="contained"
                      sx={{
                        background: isSelected
                          ? primaryGradient
                          : "action.hover",
                        color: "#fff",
                        fontWeight: 600,
                        textTransform: "none",
                        borderRadius: "10px",
                        py: 1.2,
                        boxShadow: "none",
                        border: `1px solid`,
                        borderColor: isSelected ? "transparent" : "divider",
                        "&:hover": {
                          background: isSelected
                            ? primaryGradient
                            : "action.selected",
                          opacity: 0.9,
                          boxShadow: "none",
                        },
                      }}
                    >
                      {isSelected ? "Selected" : "Select Package"}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            );
          })
        )}
      </Box>
      </Box>

      {/* Total summary */}
      <Box
        sx={{
          p: 3,
          borderRadius: "16px",
          border: `1px solid`,
          borderColor: "divider",
          backgroundColor: "background.paper",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: "text.primary", mb: 1 }}
          >
            Package Total
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <TextField
              type="text"
              value={`${editedTotal}`}
              onChange={handleTotalChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
                disableUnderline: true,
              }}
              variant="outlined"
              sx={{
                width: "140px",
                "& .MuiInputBase-input": {
                  fontSize: "1.2rem",
                  fontWeight: "bold",
                  padding: "4px 6px",
                },
              }}
            />
          </Box>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Package: ${(bookingData.package_total || 0).toFixed(2)}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Additional Services: $
            {(bookingData.additional_services_total || 0).toFixed(2)}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Button
            variant="outlined"
            sx={{
              borderColor: color400,
              color: color400,
              fontWeight: 600,
              textTransform: "none",
              borderRadius: "10px",
              px: 3,
            }}
          >
            Generate Quotation
          </Button>
          <Button
            variant="outlined"
            sx={{
              borderColor: color400,
              color: color400,
              fontWeight: 600,
              textTransform: "none",
              borderRadius: "10px",
              px: 3,
            }}
          >
            Generate Invoice
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default PackageStep;
